import axiosInstance from '../axiosInstance';

/**
 * Staff Messaging Service
 *
 * Private one-to-one chat between people in the same organisation. Entirely
 * separate from assistantService (the Ask Patch AI widget) — different
 * endpoints, different feature.
 */
const chatService = {
  // Everyone in my organisation I can message.
  getContacts: async (search = '') => {
    try {
      const query = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await axiosInstance.get(`/carehome-manager/chat/contacts${query}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error fetching contacts' };
    }
  },

  // My threads, newest first, each with an unread count.
  getConversations: async () => {
    try {
      const response = await axiosInstance.get('/carehome-manager/chat/conversations');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error fetching conversations' };
    }
  },

  // Total unread across all threads — for the sidebar badge.
  getUnreadCount: async () => {
    try {
      const response = await axiosInstance.get('/carehome-manager/chat/unread-count');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error fetching unread count' };
    }
  },

  // Find-or-create the thread with one colleague. Safe to call repeatedly —
  // the API returns the existing thread rather than making a duplicate.
  openConversation: async (userId) => {
    try {
      const response = await axiosInstance.post('/carehome-manager/chat/conversations', { user_id: userId });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error opening conversation' };
    }
  },

  /**
   * Thread messages.
   * @param {object} opts
   * @param {number} [opts.before] fetch the page older than this message id
   * @param {number} [opts.after]  fetch only what arrived after this id (polling)
   */
  getMessages: async (conversationId, opts = {}) => {
    try {
      const params = new URLSearchParams();
      if (opts.before) params.set('before', opts.before);
      if (opts.after) params.set('after', opts.after);
      if (opts.limit) params.set('limit', opts.limit);
      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await axiosInstance.get(
        `/carehome-manager/chat/conversations/${conversationId}/messages${query}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error fetching messages' };
    }
  },

  /**
   * Send text and/or attachments.
   *
   * Always multipart, even for a plain text message, so the endpoint has one
   * request shape to handle. Same header convention as the other upload
   * services here (compliance, evacuation plan) — axios fills in the boundary.
   */
  sendMessage: async (conversationId, { body = '', files = [] } = {}) => {
    try {
      const form = new FormData();
      form.append('body', body);
      files.forEach((file) => form.append('attachments', file));

      const response = await axiosInstance.post(
        `/carehome-manager/chat/conversations/${conversationId}/messages`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error sending message' };
    }
  },

  markRead: async (conversationId) => {
    try {
      const response = await axiosInstance.post(
        `/carehome-manager/chat/conversations/${conversationId}/read`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error updating read state' };
    }
  },

  deleteMessage: async (messageId) => {
    try {
      const response = await axiosInstance.delete(`/carehome-manager/chat/messages/${messageId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error deleting message' };
    }
  },
};

export default chatService;
